import { Box, Button, Menu, MenuItem, Stack, Tab, Tabs, Typography, useMediaQuery, } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import gaiaTheme from '../../../../root-components/theme';
import { BackButtonLink } from '../../../presentation-components/display-elements-components/back-button-link';
import { LoadingButton } from '@mui/lab';
import { Add, PersonAddAlt1Outlined, VisibilityOutlined } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import FileDownloadService from '../../../../shared/services/file-download/file-download.service';
import { IFileDownload } from '../../../../shared/services/file-download/file-download.model';
import CreatePdfService from '../../../../shared/services/create-pdf/create-pdf.service';
import { ShareLinkDialog } from '../../../dialogs/reporting/share-link-dialog';
import ReportPeriodService from '../../../../shared/services/report-period/report-period.service';
import { LoadingPage } from '../../loading-page';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import ReportPeriodWebpageSettingService from '../../../../shared/services/report-period-webpage-setting/report-period-webpage-setting.service';
import { IReportPeriodWebpageSetting } from '../../../../shared/services/report-period-webpage-setting/report-period-webpage-setting.model';
import { a11yProps } from '../../../../shared/utils/misc-shared-utils';
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel';
import { ReportingGaiaDownloadPdfTable } from '../../../presentation-components/reporting-components/gaia/reporting-gaia-download-pdf-table';
import { ReportingGaiaWebPageSettingsForm } from '../../../presentation-components/reporting-components/gaia/reporting-gaia-web-page-settings-form';
import { ReportContentEditor } from '../../../presentation-components/reporting-components/report-content-editor';
import { CARBON_SUMMARY_CONTENT } from '../../../../modules/public-reports/shared-services/gaia-report-content-schema';
import { InformationBox } from '../../../presentation-components/miscellaneous-components/information-box';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';

export const ReportingGaiaReportDetailPage = () => {
  const { id } = useParams();
  //Services
  const createPdfService = new CreatePdfService();
  const fileDownloadService = new FileDownloadService()
  const reportPeriodService = new ReportPeriodService()
  const reportPeriodWebpageSettingService = new ReportPeriodWebpageSettingService();
  const userNotificationsContext = useContext(UserNotificationsContext)
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  const reportPeriodContext = useContext(ReportPeriodContext)
  //State
  const [fileDownloads, setFileDownloads] = useState<IFileDownload[]>()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [openPublishedDialog, setOpenPublishedDialog] = useState<boolean>(false)
  const [tabValue, setTabValue] = useState(0);
  const [reportPeriodWebpageSettings, setReportPeriodWebpageSettings] = useState<IReportPeriodWebpageSetting | null>(null);
  //Table State
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  //Loading State
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  //Variables
  const reportPeriod = reportPeriodContext.allReportPeriods.find(reportPeriod => reportPeriod.id === id)
  const openDropdown = Boolean(anchorEl)
  const companyName = (!userDetails?.isPartner && userDetails?.partnerOrganisation) || (userDetails?.isPartner && userDetails?.isLoggedInAsClient) ? userDetails?.partnerOrganisation?.partnerName : 'Gaia';
  //Use Effects
  useEffect(() => {
    if (tabValue === 0 || tabValue === 1) {
      getReportPeriodWebpageSettings()
    } else if (tabValue === 2) {
      getDownloads();
    }
  }, [inputPage, perPage, id, tabValue])

  const saveReportField = async (field: string, value: any) => {
    if (!id) return;
    const base = reportPeriodWebpageSettings || ({ reportPeriodId: id, organisationId: userDetails?.organisation?.id } as any);
    const updated = { ...base, [field]: value };
    try {
      const settings = await reportPeriodWebpageSettingService.updateSettings(id, updated);
      setReportPeriodWebpageSettings(settings);
      userNotificationsContext.openSnackbar({ open: true, message: 'Updated Successfully' });
      return settings;
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: '#70787A' });
      return null;
    }
  };


  const getReportPeriodWebpageSettings = () => {
    if (id) {
      reportPeriodWebpageSettingService.getReportPeriodWebpageSettings(id)
        .then((settings: IReportPeriodWebpageSetting) => {
          setReportPeriodWebpageSettings(settings);
          setIsLoading(false);
        })
        .catch((err: any) => {
          setIsLoading(false);
        });
    }
  }

  const getDownloads = () => {
    if (id) {
      fileDownloadService.getAllWithPagination(id, 'gaia', inputPage, perPage)
        .then(response => {
          setIsLoading(false)
          setFileDownloads(response.rows)
          setTotalRecords(response.count);
        })
        .catch(err => {
          setIsLoading(false)
          console.log(err)
        })
    }
  }

  //Component Event Handlers
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const viewWebpageHandler = () => {
    if (!userDetails?.organisation.subscriptionActive || (userDetails.organisation.subscriptionActive && userDetails.organisation.freeTrialDaysRemaining && userDetails.organisation.freeTrialDaysRemaining > 0)) {
      userNotificationsContext.openDialog({ active: true, hideCancelButton: true, title: 'Access Denied', message: `You must have a subscription to ${companyName} Carbon Accounting to be able to view your ${companyName} Carbon Summary Webpage.`, buttonCopy: 'Ok', })
    } else {
      window.open(`/private/carbon-summary-report/${id}`, '_blank', 'noopener,noreferrer');
    }
  }

  const publishWebpageHandler = () => {
    if (!userDetails?.organisation.subscriptionActive || (userDetails.organisation.subscriptionActive && userDetails.organisation.freeTrialDaysRemaining && userDetails.organisation.freeTrialDaysRemaining > 0)) {
      userNotificationsContext.openDialog({ active: true, hideCancelButton: true, title: 'Access Denied', message: `You must have a subscription to ${companyName} Carbon Accounting to be able to view your ${companyName} Carbon Summary Webpage.`, buttonCopy: 'Ok', })
    } else {
      userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to publish this webpage? Once published, anyone with the link will be able to view it.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
        .then(response => {
          if (response && id) {
            reportPeriodService.publishGaiaReportWebpage(id)
              .then(response => {
                userNotificationsContext.openSnackbar({ open: true, message: 'Published successfully' })
                reportPeriodContext.forceUpdateReportPeriods()
              })
              .catch(err => {
                console.log(err)
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
              })
          }
        })
    }
  }

  const unpublishWebpageHandler = () => {
    if (!userDetails?.organisation.subscriptionActive || (userDetails.organisation.subscriptionActive && userDetails.organisation.freeTrialDaysRemaining && userDetails.organisation.freeTrialDaysRemaining > 0)) {
      userNotificationsContext.openDialog({ active: true, hideCancelButton: true, title: 'Access Denied', message: `You must have a subscription to ${companyName} Carbon Accounting to be able to view your ${companyName} Carbon Summary Webpage.`, buttonCopy: 'Ok', })
    } else {
      userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to unpublish this webpage? Anyone with the link will be no longer able to view it.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
        .then(response => {
          if (response && id) {
            reportPeriodService.unpublishGaiaReportWebpage(id)
              .then(response => {
                userNotificationsContext.openSnackbar({ open: true, message: 'Unublished successfully' })
                reportPeriodContext.forceUpdateReportPeriods()
              })
              .catch(err => {
                console.log(err)
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
              })
          }
        })
    }
  }

  const shareWebpageHandler = () => {
    if (!userDetails?.organisation.subscriptionActive || (userDetails.organisation.subscriptionActive && userDetails.organisation.freeTrialDaysRemaining && userDetails.organisation.freeTrialDaysRemaining > 0)) {
      userNotificationsContext.openDialog({ active: true, hideCancelButton: true, title: 'Access Denied', message: `You must have a subscription to ${companyName} Carbon Accounting to be able to view your ${companyName} Carbon Summary Webpage.`, buttonCopy: 'Ok', })
    } else {
      setOpenPublishedDialog(true)
    }
  }

  const reportHandler = () => {
    if (!userDetails?.organisation.subscriptionActive || (userDetails.organisation.subscriptionActive && userDetails.organisation.freeTrialDaysRemaining && userDetails.organisation.freeTrialDaysRemaining > 0)) {
      userNotificationsContext.openDialog({ active: true, hideCancelButton: true, title: 'Access Denied', message: `You must have a subscription to ${companyName} Carbon Accounting to be able to download your ${companyName} Carbon Summary Report.`, buttonCopy: 'Ok', })
    }
    else {
      if (!isButtonLoading) {
        setIsButtonLoading(true)
        createPdfService.createGaiaReportPdf(companyName || 'Gaia', `?reportPeriodId=${id}`)
          .then(response => {
            getDownloads();
            setIsButtonLoading(false)
          })
          .catch(err => {
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            setIsButtonLoading(false)
          })
      }
    }
  }

  const isReportLocked = Boolean(reportPeriodContext.allReportPeriods.find(r => r.id === id)?.locked);
  const isReportPublic = Boolean(reportPeriodContext.allReportPeriods.find(r => r.id === id)?.isGaiaReportWebpagePublic);

  const webpageActions = (
    <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
      <Button
        id="basic-button"
        disabled={!isReportLocked}
        aria-controls={openDropdown ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={openDropdown ? 'true' : undefined}
        onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
        sx={{ mr: 2 }}>
        <PersonAddAlt1Outlined sx={{ mr: 1 }} />
        <Typography>Share Webpage</Typography>
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={openDropdown}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ 'aria-labelledby': 'basic-button' }}>
        {!isReportPublic && <MenuItem onClick={publishWebpageHandler}>Publish Webpage</MenuItem>}
        {isReportPublic && (
          <>
            <MenuItem onClick={unpublishWebpageHandler}>Unpublish Webpage</MenuItem>
            <MenuItem onClick={shareWebpageHandler}>Share Webpage</MenuItem>
          </>
        )}
      </Menu>
      <Button onClick={viewWebpageHandler} sx={{ mr: 2 }} variant='text'>
        <VisibilityOutlined sx={{ mr: 1 }} />
        <Typography>View Webpage</Typography>
      </Button>
    </Box>
  );

  const lockedInfoBox = !isReportLocked && (
    <Stack sx={{ mb: 2 }}>
      <InformationBox
        text='The webpage cannot be shared as the report period has not yet closed'
        icon={IconName.InfoOutlined}
      />
    </Stack>
  );

  return (
    <PageOuterContainer>
      <Stack display='flex' justifyContent='flex-start'>
        <BackButtonLink buttonText={'Back to carbon summaries'} link={'/reporting/carbon-summary-report'} />
        <Box sx={{ display: 'flex', flexDirection: isMobileMode ? 'column' : 'row', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 1 }}>
          <Typography variant='h4' sx={{ color: 'text.primary' }}>{(!userDetails?.isPartner && userDetails?.partnerOrganisation) || (userDetails?.isPartner && userDetails?.isLoggedInAsClient) ? 'Carbon Summary Report' : 'Gaia Carbon Summary Report'} ({reportPeriod?.friendlyName})</Typography>
          {/* <Stack display='flex' flexDirection='row' alignItems={'center'}>
            <Button
              id="basic-button"
              variant='text'
              disabled={!reportPeriodContext.allReportPeriods.find(r => r.id === id)?.locked}
              aria-controls={openDropdown ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={openDropdown ? 'true' : undefined}
              onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
              sx={{ mr: 2 }}>
              <PersonAddAlt1Outlined sx={{ mr: 1 }} />
              <Typography >Share Webpage</Typography>
            </Button>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={openDropdown}
              onClose={() => setAnchorEl(null)}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}>
              {!reportPeriodContext.allReportPeriods.find(r => r.id === id)?.isGaiaReportWebpagePublic &&
                <MenuItem onClick={publishWebpageHandler}>Publish Webpage</MenuItem>
              }
              {reportPeriodContext.allReportPeriods.find(r => r.id === id)?.isGaiaReportWebpagePublic &&
                <>
                  <MenuItem onClick={unpublishWebpageHandler}>Unpublish Webpage</MenuItem>
                  <MenuItem onClick={shareWebpageHandler}>Share Webpage</MenuItem>
                </>}
            </Menu>
            <Button onClick={viewWebpageHandler} sx={{ mr: 2 }}>
              <VisibilityOutlined sx={{ mr: 1 }} />
              <Typography>View Webpage</Typography>
            </Button>
          </Stack> */}
        </Box>
      </Stack>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="Report tabs">
            <Tab label="Content" {...a11yProps(0)} />
            <Tab label="Design" {...a11yProps(1)} />
            <Tab label="PDF Reports" {...a11yProps(2)} />
          </Tabs>
        </Box>
        <CustomTabPanel value={tabValue} index={0}>
          {webpageActions}
          {lockedInfoBox}
          {id && reportPeriodWebpageSettings &&
            <ReportContentEditor
              areas={CARBON_SUMMARY_CONTENT}
              settings={reportPeriodWebpageSettings}
              saveField={saveReportField}
            />}
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={1}>
          {webpageActions}
          {lockedInfoBox}
          {id && reportPeriodWebpageSettings &&
            <ReportingGaiaWebPageSettingsForm
              reportPeriodWebpageSettings={reportPeriodWebpageSettings}
              setReportPeriodWebpageSettings={setReportPeriodWebpageSettings}
              id={id}
            />}
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <LoadingButton
              loading={isButtonLoading}
              loadingPosition="start"
              startIcon={<Add />}
              variant="text"
              onClick={reportHandler}
              disabled={fileDownloads?.some(file => file.isFinalVersion) || isButtonLoading}>
              <Typography>{isButtonLoading ? 'Creating New PDF Report' : 'Create New PDF Report'}</Typography>
            </LoadingButton>
          </Box>
          <LoadingPage pageReady={!isLoading}>
            {fileDownloads &&
              <ReportingGaiaDownloadPdfTable
                fileDownloads={fileDownloads}
                getDownloads={getDownloads}
                inputPage={inputPage}
                setInputPage={setInputPage}
                totalRecords={totalRecords}
                perPage={perPage}
                setPerPage={setPerPage}
              />
            }
          </LoadingPage>
        </CustomTabPanel>
      </Box>
      {
        openPublishedDialog &&
        <ShareLinkDialog
          open={openPublishedDialog}
          link={`${process.env.REACT_APP_APP_URL}/public/carbon-summary-report/${id}`}
          closeHandler={() => setOpenPublishedDialog(false)}
          isPublic={true} />
      }
    </PageOuterContainer >
  )
}